import { useCallback, useEffect, useMemo, useState } from 'react';
import { SEARCH_DEBOUNCE_DELAY } from 'src/constants/delays';
import {
  PreparedConversationParticipantOption,
  useConversationParticipantOptions,
} from 'src/graphql';
import { useDebouncedValue } from 'src/utils/hooks/use-debounced-value';

// Menu order for the participant groups; anything unmapped sorts last.
const GROUP_ORDER = ['Clients', 'Agents', 'RSM', 'Unidentified'];

/**
 * Menu state (open, search text) plus the server-backed participant options for the
 * current search, grouped in menu order. `options` is empty until the response for the
 * text currently in the box has landed; `allOptions` is the unguarded list, for resolving
 * a picked id.
 */
export const useParticipantOptionsSearch = ({
  partnerId,
  skipClients,
}: {
  partnerId: number;
  skipClients?: boolean;
}) => {
  const { getConversationParticipantOptions, conversationParticipantOptions, loading } =
    useConversationParticipantOptions();

  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search, SEARCH_DEBOUNCE_DELAY);
  // When search is cleared, fetch empty immediately — don't wait out the debounce,
  // or reopen would request the previous query while the input already shows "".
  const searchText = search === '' ? '' : debouncedSearch;

  // `useConversationParticipantOptions` falls back to Apollo's previousData so results stay
  // put while the next query loads, which means the visible rows can belong to an earlier
  // search. It returns void instead of the request promise, so there is no promise to await
  // or abort here; recording the search each request was issued for — and clearing that
  // record on cleanup — is the ignore guard: a response that lands after a newer search, or
  // after close/unmount, matches nothing and stays hidden.
  const [requestedSearch, setRequestedSearch] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      return;
    }
    setRequestedSearch(searchText);
    getConversationParticipantOptions({ partnerId, searchText, skipClients }, true);

    return () => setRequestedSearch(null);
  }, [open, searchText, partnerId, skipClients, getConversationParticipantOptions]);

  const options = useMemo<PreparedConversationParticipantOption[]>(() => {
    if (loading || requestedSearch !== searchText) {
      return [];
    }
    return [...conversationParticipantOptions].sort(
      (a, b) => GROUP_ORDER.indexOf(a.group) - GROUP_ORDER.indexOf(b.group),
    );
  }, [conversationParticipantOptions, loading, requestedSearch, searchText]);

  // Controlled search means the reset is ours. Clear on close; `searchText`
  // treats "" as immediate so reopen fetches empty without debounce lag.
  const handleOpenChange = useCallback((next: boolean) => {
    setOpen(next);
    if (!next) {
      setSearch('');
    }
  }, []);

  return {
    open,
    search,
    loading,
    options,
    allOptions: conversationParticipantOptions,
    setSearch,
    handleOpenChange,
  };
};
